import qs from 'query-string';
import { ReactElement, ReactNode, createElement } from 'react';
import {
  LoaderFunction,
  LoaderFunctionArgs,
  Outlet,
  Params,
  RouteObject,
  redirect,
  useLoaderData,
} from 'react-router';

import { fetchUnreviewed } from '@/consultation/store';
import agreements, { REQUIRED_EPA_VERSION, isAgreementGateDisabled } from '@/core/agreements';
import { GraphQLClient } from '@/core/api';
import { PermissionService } from '@/core/permissions';
import { hasAcceptedEmail } from '@/core/user';
import type { Viewer } from '@/core/viewer';
import SendbirdHelper from '@/messaging/sendbird';
import { AppStore } from '@/store';
import { parseHostname, pathAndQuery } from '@/utils';

export interface MiddlewareContext {
  store: AppStore;
  graphqlClient: GraphQLClient;
  sendbird: SendbirdHelper;
  permission: PermissionService;
}

export interface ActionContext extends MiddlewareContext {
  request: Request;
  params: Params;
  location: URL;
  path: string;
  subdomain: string | null | undefined;
  query: URLSearchParams;
}

export interface LegacyRoute {
  path: string;
  public?: boolean;
  title?: string;
  element?: ReactElement;
  children?: Array<LegacyRoute>;
  action?: (context: ActionContext) => Promise<any>;
}

export type RouteMiddleware = (context: MiddlewareContext) => LoaderFunction;

export function parseQueryBool(query: URLSearchParams, key: string): boolean {
  return query.get(key) === 'true';
}

// The review emails emit exactly one digit, 1-5. Match the raw param exactly
// rather than parsing it as an int, which is permissive ('5abc' yields 5).
// This is the only client-side check before persisting; api#1746 added the
// matching 1-5 bound server-side.
export function parseRatingParam(query: URLSearchParams, key: string): number | undefined {
  const value = query.get(key);
  return value !== null && /^[1-5]$/.test(value) ? Number(value) : undefined;
}

export function redirectIfEmailNotVerified(store: AppStore) {
  const { viewer } = store.getState();

  if (!hasAcceptedEmail(viewer)) {
    return redirect('/awaiting_email_validation');
  }
}

export function applyMiddlewares(
  context: MiddlewareContext,
  middlewares: RouteMiddleware[]
): LoaderFunction {
  const loaders = middlewares.map((middleware) => middleware(context));
  return async (args: LoaderFunctionArgs) => {
    for (const loader of loaders) {
      let resp = loader(args);
      if (resp instanceof Promise) {
        resp = await resp;
      }
      if (resp !== undefined) return resp;
    }
  };
}

// Routes that stay reachable while the gate is active. Gating any of these is
// a lockout with no way out: /legal/accept is the gate itself, the three
// email-validation paths are the links in the verification email, and
// /network/ is an accept-or-reject invitation — refusing work is not something
// the gate should block.
export const GATE_EXEMPT_PREFIXES = [
  '/legal/accept',
  '/legal_ack',
  '/login',
  '/loginas',
  '/validate_email',
  '/awaiting_email_validation',
  '/email_validated',
  '/network/',
];

export function isGateExempt(pathname: string): boolean {
  return GATE_EXEMPT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// ENG-5570. Experts must accept the current Expert Participation Agreement
// before reaching the product.
//
// Only the EPA is gated. This used to also check terms-of-use and privacy for
// *every* viewer, which would lock out any customer, admin or staff account
// missing those rows — a population that includes anyone created by invitation
// or by an admin, since those paths never post an agreements array. Whether
// those two should ever gate is ENG-5563's call.
/**
 * Whether this viewer owes an acceptance, independent of where they are.
 *
 * The gate and the gate page both ask this, from opposite sides: the gate
 * redirects when it is true, /legal/accept redirects away when it is false.
 * Keeping it in one place is what stops them disagreeing, which is how the page
 * ended up reachable by every logged-in user and signable by customers.
 */
export function owesAgreementAcceptance(viewer: Viewer): boolean {
  // Both brakes, read first and read explicitly. hasAcceptedVersion below
  // already honours them, so this is not load-bearing today — it is here so
  // that the release path is the first thing anyone reads in the function they
  // open mid-incident, rather than three calls down. ENG-5601.
  if (isAgreementGateDisabled()) return false;

  // No required version means nothing to gate on. REQUIRED_EPA_VERSION is set
  // as of Rev 2.1, so this branch is the revert path rather than the shipped
  // state: nulling the constant stands the gate down everywhere at once.
  if (!REQUIRED_EPA_VERSION) return false;

  if (!viewer.id) return false;

  // An account that hasn't proven it owns its email address shouldn't be asked
  // for a legally binding click, so defer to the email check rather than
  // collecting consent first.
  //
  // That deferral is only complete where the email check actually follows:
  // allRequiredLegacy, allRequiredMiddleware and agreementsWithEmailRequiredLegacy.
  // Five routes take agreementsRequiredLegacy on its own — browse, graphiql,
  // expertRequest, profile and register — and on those an active expert with an
  // unverified email is neither gated here nor stopped afterwards. That reaches
  // /expert_request/:id/request_add, the "add me as a candidate" route this
  // ticket gates precisely because it is the participation the EPA governs.
  //
  // Left as-is deliberately: the alternative is asking for a binding signature
  // from an account that has not proven it owns its address. Do NOT "fix" it by
  // wrapping those five in agreementsWithEmailRequiredLegacy — browse and
  // profile are reachable anonymously, and the email check would redirect every
  // logged-out visitor to /awaiting_email_validation. ENG-5570 follow-up.
  if (!hasAcceptedEmail(viewer)) return false;

  // expert_state resolves the viewer's own record, so it's trustworthy here.
  // Preferred over signup_type, which is signup-time intent: it misses experts
  // who signed up as clients, and catches people who never activated.
  if (viewer.expert_state !== 'active') return false;

  return !agreements(viewer.agreements).hasAcceptedVersion(REQUIRED_EPA_VERSION);
}

export function redirectIfAgreementsNotAccepted(
  store: AppStore,
  location: URL
): Response | undefined {
  if (isGateExempt(location.pathname)) return;
  if (!owesAgreementAcceptance(store.getState().viewer)) return;
  return redirect(`/legal/accept?next=${pathAndQuery(location)}`);
}

/**
 * Guards /legal/accept itself. Without it the page is a forced-consent surface:
 * any logged-in customer sent the URL sees a full-screen expert agreement with
 * one tick and no explanation, and writes a real acceptance record. It is also
 * where the ?next= redirect is read, so leaving it open exposes that to
 * everyone rather than to gated experts alone.
 */
export function agreementGateOnlyMiddleware({ store }: MiddlewareContext): LoaderFunction {
  return () => {
    if (owesAgreementAcceptance(store.getState().viewer)) return undefined;
    return redirect('/dashboard');
  };
}

/**
 * Core login check logic shared by both middleware and legacy routes.
 * Returns a redirect Response if login is required, undefined otherwise.
 */
async function checkLoginRequired(
  store: AppStore,
  request: Request
): Promise<Response | undefined> {
  const { viewer } = store.getState();
  const location = new URL(request.url);

  if (!viewer.id) {
    return redirect(`/login?next=${pathAndQuery(location)}`);
  }

  await store.dispatch(fetchUnreviewed());
}

export function loginRequiredMiddleware({ store }: MiddlewareContext): LoaderFunction {
  return async ({ request }) => checkLoginRequired(store, request);
}

export function loginRequiredLegacy(route: LegacyRoute): LegacyRoute {
  if (route.public) return route;

  return {
    ...route,
    async action(...args: [ActionContext]) {
      const [context] = args;
      const redirectResult = await checkLoginRequired(context.store, context.request);

      return redirectResult || (route.action && route.action(...args));
    },
  };
}

export function agreementsRequiredMiddleware({ store }: MiddlewareContext): LoaderFunction {
  return async ({ request }) => {
    const location = new URL(request.url);
    return redirectIfAgreementsNotAccepted(store, location);
  };
}

export function agreementsRequiredLegacy(route: LegacyRoute): LegacyRoute {
  return {
    ...route,

    async action(...args: [ActionContext]) {
      const [context] = args || [];
      const location = new URL(context.request.url);

      return (
        redirectIfAgreementsNotAccepted(context.store, location) ||
        (route.action && route.action(...args))
      );
    },
  };
}

export function verifiedEmailRequiredMiddleware({ store }: MiddlewareContext): LoaderFunction {
  return async () => {
    return redirectIfEmailNotVerified(store);
  };
}

export function verifiedEmailRequiredLegacy(route: LegacyRoute): LegacyRoute {
  return {
    ...route,

    async action(...args: [ActionContext]) {
      const [context] = args || [];

      return redirectIfEmailNotVerified(context.store) || (route.action && route.action(...args));
    },
  };
}

export function superAdminRequiredLegacy(route: LegacyRoute): LegacyRoute {
  return {
    ...route,

    async action(...args: [ActionContext]) {
      const [context] = args || [];
      const { viewer } = context.store.getState();

      if (!viewer.id) {
        return redirect('/');
      }

      const canViewAdminPanel = await context.permission.allowed(
        'profile',
        'super_admin',
        viewer.id
      );
      if (!canViewAdminPanel) {
        return redirect('/');
      }

      return route.action && route.action(...args);
    },
  };
}

export function agreementsWithEmailRequiredLegacy(route: LegacyRoute): LegacyRoute {
  return agreementsRequiredLegacy(verifiedEmailRequiredLegacy(route));
}

export function allRequiredMiddleware(context: MiddlewareContext): LoaderFunction {
  return applyMiddlewares(context, [
    loginRequiredMiddleware,
    agreementsRequiredMiddleware,
    verifiedEmailRequiredMiddleware,
  ]);
}

export function allRequiredLegacy(route: LegacyRoute): LegacyRoute {
  return loginRequiredLegacy(agreementsRequiredLegacy(verifiedEmailRequiredLegacy(route)));
}

/**
 * Maps query parameters to an object with key value pairs
 * @param {string} url
 * @returns {Object<string, string>}
 */

export function mapQueryParams(url: string): URLSearchParams {
  const parsedParams = qs.parse(new URL(url).search, { arrayFormat: 'bracket' });

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(parsedParams)) {
    if (Array.isArray(value)) {
      for (const v of value) {
        searchParams.append(key, v);
      }
    } else if (value !== null) {
      searchParams.append(key, value);
    }
  }
  return searchParams;
}

export type ProtectionFunction = (route: LegacyRoute) => LegacyRoute;

const LegacyCompat = () => {
  const data = useLoaderData() as ReactNode;
  // Fall back to the outlet when the loader produced nothing.
  //
  // A protection function adds an `action` to every route it wraps, including
  // parent routes that have children but no action of their own (`/profile`,
  // `/expert_request`). That synthesised action returns undefined once the
  // guard passes, but it is enough to give the parent a loader, and a loader
  // is what makes the adapter below hand the parent an element. React Router
  // then renders that element *instead of* the matched child, so without this
  // fallback wrapping a parent route silently blanks its entire subtree.
  return data ?? createElement(Outlet);
};

export function legacyRouteAdapter(
  context: MiddlewareContext,
  route: LegacyRoute,
  protectionFn?: ProtectionFunction
): RouteObject {
  const protectedRoute = protectionFn ? protectionFn(route) : route;

  let loader: LoaderFunction | undefined;
  const action = protectedRoute.action;
  if (action) {
    loader = ({ params, request }) => {
      const url = new URL(request.url);
      const { subdomain } = parseHostname(url.hostname);
      return action({
        ...context,
        request,
        params,
        location: url,
        path: url.pathname,
        subdomain: subdomain === 'www' ? null : subdomain,
        query: mapQueryParams(request.url),
      });
    };
  }

  return {
    path: protectedRoute.path,
    element: protectedRoute.element || (loader ? createElement(LegacyCompat) : undefined),
    loader,
    children: (protectedRoute.children || []).map((child) =>
      legacyRouteAdapter(context, child, protectionFn)
    ),
  };
}
